import { kebabCase } from 'lodash-es';
import { GlCollapse, GlDisclosureDropdown, GlDisclosureDropdownGroup, GlIcon, GlNavItem, GlAnimatedChevronRightDownIcon, GlOutsideDirective as Outside, GlTooltipDirective } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { HIDDEN_NAV_ITEM_CLASS, DEFAULT_PIN_CONTEXT } from '../constants';
import NavItem from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/nav_item.vue?vue3';
import FlyoutMenu from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/flyout_menu.vue?vue3';
import DisclosureNavItem from '/builds/gitlab/gitlab-ee/app/assets/javascripts/super_sidebar/components/disclosure_nav_item.vue?vue3';
export default {
    name: 'MenuSection',
    components: {
        GlCollapse,
        GlDisclosureDropdown,
        GlDisclosureDropdownGroup,
        GlIcon,
        GlNavItem,
        GlAnimatedChevronRightDownIcon,
        NavItem,
        FlyoutMenu,
        DisclosureNavItem
    },
    directives: {
        Outside,
        GlTooltip: GlTooltipDirective
    },
    inject: {
        isIconOnly: {
            default: false
        }
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        expanded: {
            type: Boolean,
            required: false,
            default: false
        },
        tag: {
            type: String,
            required: false,
            default: 'div'
        },
        hasFlyout: {
            type: Boolean,
            required: false,
            default: false
        },
        disclosure: {
            type: Boolean,
            required: false,
            default: false
        },
        // Forces the disclosure toggle to render as an icon-only button (with a
        // tooltip) even in the expanded sidebar, for use where header space is
        // tight. Only affects the disclosure toggle.
        iconOnlyToggle: {
            type: Boolean,
            required: false,
            default: false
        },
        pinContext: {
            type: Object,
            required: false,
            default: ()=>({
                    ...DEFAULT_PIN_CONTEXT
                })
        },
        headerless: {
            type: Boolean,
            required: false,
            default: false
        },
        boldTitle: {
            type: Boolean,
            required: false,
            default: false
        },
        // Hides the chevron until the header is hovered or keyboard-focused, on devices
        // that can hover. Touch devices always show it.
        revealChevronOnHover: {
            type: Boolean,
            required: false,
            default: false
        },
        asyncCount: {
            type: Object,
            required: false,
            default: ()=>({})
        }
    },
    emits: [
        'collapse-toggle',
        'nav-link-click',
        'pin-add',
        'pin-remove'
    ],
    data () {
        return {
            isExpanded: Boolean(this.headerless || this.expanded || this.item.is_active),
            isMouseOverSection: false,
            isMouseOverFlyout: false,
            keepFlyoutClosed: false
        };
    },
    computed: {
        navItems () {
            return this.item.items.filter((item)=>{
                if (item.link_classes) {
                    return !item.link_classes.includes(HIDDEN_NAV_ITEM_CLASS);
                }
                return true;
            });
        },
        buttonProps () {
            return {
                'aria-controls': this.itemId,
                'aria-expanded': String(this.isExpanded),
                'data-qa-menu-item': this.item.title
            };
        },
        computedLinkClasses () {
            return {
                'with-mouse-over-flyout': this.isMouseOverFlyout,
                'super-sidebar-category-toggle': this.revealChevronOnHover
            };
        },
        isActive () {
            return (!this.isExpanded || this.isIconOnly) && this.item.is_active;
        },
        itemId () {
            return kebabCase(this.item.title);
        },
        isMouseOver () {
            return this.isMouseOverSection || this.isMouseOverFlyout;
        },
        showExpanded () {
            return !this.isIconOnly && this.isExpanded;
        },
        toggleIsIconOnly () {
            return this.iconOnlyToggle || this.isIconOnly;
        },
        // Only icon-only toggles need a tooltip (the label is hidden). Anchor it
        // above the button in the header (forced icon-only via iconOnlyToggle) and
        // to the right in the collapsed sidebar (injected isIconOnly).
        toggleTooltip () {
            if (!this.toggleIsIconOnly) return {
                title: ''
            };
            if (this.iconOnlyToggle) {
                // Keep the header tooltip from being clipped at the top of the viewport.
                return {
                    title: this.item.title,
                    placement: 'top',
                    boundary: 'viewport'
                };
            }
            return {
                title: this.item.title,
                placement: 'right'
            };
        },
        // A headerless section has no toggle to reveal its items, so keep the
        // collapse open regardless of the (stale) expanded state carried over from
        // the expanded sidebar.
        collapseVisible () {
            return this.headerless || this.isExpanded;
        },
        showFlyout () {
            return !this.headerless && this.hasFlyout && this.isMouseOver && !this.showExpanded && !this.keepFlyoutClosed && this.navItems.length > 0;
        }
    },
    watch: {
        isExpanded (newIsExpanded) {
            this.$emit('collapse-toggle', newIsExpanded);
            this.keepFlyoutClosed = !newIsExpanded && !this.isIconOnly;
            if (!newIsExpanded) {
                this.isMouseOverFlyout = false;
            }
        },
        isIconOnly (newIsIconOnly) {
            // Reset keepFlyoutClosed when toggling between expanded/collapsed sidebar
            if (newIsIconOnly) {
                this.keepFlyoutClosed = false;
            }
        },
        headerless (newHeaderless) {
            // A headerless section is force-expanded, so isExpanded may have been
            // initialized to true regardless of the real (cookie-backed) state. When
            // the header reappears, re-sync to the actual expanded prop so the group
            // honors its own collapse state instead of staying stuck open.
            if (!newHeaderless) {
                this.isExpanded = Boolean(this.expanded || this.item.is_active);
            }
        }
    },
    methods: {
        onCollapseInput (visible) {
            // Ignore the forced-open state of a headerless section so its stale
            // expanded state (from the expanded sidebar) is preserved.
            if (this.headerless) return;
            this.isExpanded = visible;
        },
        handleClick () {
            if (this.isIconOnly) {
                this.isMouseOverSection = !this.isMouseOverSection; // Allows touch devices to open the flyout menus by touch
                return;
            }
            this.isExpanded = !this.isExpanded;
        },
        handleClickOutside (targetId) {
            this.isMouseOverSection = false; // Allows touch devices to close the flyout menus by touch
            if (targetId) {
                document.getElementById(targetId)?.focus();
            }
        },
        handlePointerover (e) {
            if (!this.hasFlyout) return;
            this.isMouseOverSection = e.pointerType === 'mouse' || e.pointerType === 'pen';
        },
        handlePointerleave (e) {
            if (!this.hasFlyout) return;
            this.keepFlyoutClosed = false;
            // delay state change. otherwise the flyout menu gets removed before it
            // has a chance to emit its mouseover event.
            // checks pointer type to not mess with touch devices, which fire a pointerleave event before
            // every click!
            if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
                setTimeout(()=>{
                    this.isMouseOverSection = false;
                }, 5);
            }
        }
    }
};
